/**
 * GovConversas — feed de conversas ao vivo do WhatsApp institucional.
 * Reusa o backend `/api/conversations` compartilhado. UI própria GOV
 * com selos de secretaria, SLA e canal oficial.
 *
 * v0.99.9 · Wave 2 · Fev/2026
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChatCircleText, WhatsappLogo, Circle, ArrowClockwise, MagnifyingGlass,
  Clock, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

export default function GovConversas() {
  const nav = useNavigate();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/conversations", { params: { limit: 50 } });
      setConvs(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error("Falha ao carregar conversas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 30000); // auto-refresh 30s
    return () => clearInterval(t);
  }, []);

  const filtered = convs.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.customer_name || "").toLowerCase().includes(s)
        || (c.last_message || "").toLowerCase().includes(s);
  });

  const active = filtered.filter(c => c.status !== "closed" && c.status !== "resolved");
  const closed = filtered.filter(c => c.status === "closed" || c.status === "resolved");

  return (
    <div data-testid="gov-conversas-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">
            Atendimento
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <ChatCircleText size={22} className="text-emerald-700" />
            Conversas ao vivo
          </h1>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Feed em tempo real dos atendimentos WhatsApp do órgão · atualiza a cada 30s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-medium">
            <WhatsappLogo size={12} weight="fill" /> Canal Oficial
          </span>
          <button onClick={load} data-testid="conversas-refresh" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-[12px] text-slate-700">
            <ArrowClockwise size={12} /> Atualizar
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Buscar por cidadão ou mensagem…"
            data-testid="conversas-search"
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none"
          />
        </div>
      </div>

      {loading && <div className="text-center py-12 text-slate-400 text-[12.5px]">Carregando…</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 rounded-xl border border-dashed border-slate-300 bg-white">
          <ChatCircleText size={32} className="mx-auto mb-2 text-slate-300" />
          <div className="text-[13px] font-medium text-slate-500">Nenhuma conversa em andamento</div>
          <div className="text-[11.5px] text-slate-400 mt-1">Assim que um cidadão iniciar contato no WhatsApp, a conversa aparecerá aqui.</div>
        </div>
      )}

      {!loading && active.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-emerald-800">
              Em atendimento ({active.length})
            </div>
            <div className="flex-1 h-px bg-emerald-100" />
          </div>
          <ConvList items={active} nav={nav} />
        </>
      )}

      {!loading && closed.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2 mt-6">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
              Resolvidos ({closed.length})
            </div>
            <div className="flex-1 h-px bg-slate-100" />
          </div>
          <ConvList items={closed} nav={nav} muted />
        </>
      )}
    </div>
  );
}

function ConvList({ items, nav, muted = false }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
      {items.map((c) => {
        const status = (c.status || "").toLowerCase();
        const isPending = status === "waiting_agent" || status === "pending";
        const isResolved = status === "resolved" || status === "closed";
        const StatusIcon = isResolved ? CheckCircle : isPending ? WarningCircle : Circle;
        const statusColor = isResolved ? "text-slate-400" : isPending ? "text-amber-600" : "text-emerald-600";
        return (
          <button
            key={c.id}
            onClick={() => nav(`/gov/conversas/${c.id}`)}
            data-testid={`conv-row-${c.id}`}
            className={`w-full text-left px-4 py-3 hover:bg-emerald-50/40 transition-colors flex items-center gap-3 ${muted ? "opacity-70" : ""}`}
          >
            <div className={`shrink-0 ${statusColor}`}>
              <StatusIcon size={16} weight={isResolved ? "fill" : "regular"} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-slate-900 text-[13px] truncate">
                  {c.customer_name || c.customer_phone || "Cidadão desconhecido"}
                </div>
                <div className="text-[10.5px] text-slate-400 shrink-0 tabular-nums flex items-center gap-1">
                  <Clock size={10} />
                  {c.last_message_at ? new Date(c.last_message_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
              </div>
              {c.last_message && (
                <div className="text-[11.5px] text-slate-500 truncate mt-0.5">
                  {c.last_message}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
